/**
 * Thermal Printer Service - Main Orchestrator
 * Auto-detects and uses the best available printing method:
 * 1. QZ Tray (USB/Network/Bluetooth via QZ)
 * 2. Web Bluetooth (Direct Bluetooth)
 * 3. Browser Print (Fallback)
 */
import dayjs from 'dayjs'
import qzTrayPrinter from './qzTrayPrinter'
import browserPrinter from './browserPrinter'

// Print methods priority
export const PRINT_METHODS = {
  QZ_TRAY: 'qz-tray',
  BLUETOOTH: 'bluetooth',
  BROWSER: 'browser',
}

// Storage key for user preference
const STORAGE_KEY = 'thermal_printer_preference'

export const mockReceiptData = {
  ref_number: 'INV-2024-001',
  trans_date: dayjs().toISOString(),
  user: { name: 'Kasir Test' },
  contact: { name: 'Pelanggan Test Uji Coba' },
  orderType: { id: 1, name: 'Dine In' },
  items: [
    {
      id: 1,
      product: { name: 'Nasi Goreng Spesial' },
      qty: 2,
      price: 25000,
      amount: 50000,
      discount_amount: 0,
      desc: 'Tidak pedas',
      price_rule: null,
    },
  ],
  amount: 50000, // Subtotal sebelum diskon
  discount_per_item: 5000, // Total diskon per item
  additional_discount_amount: 0,
  additional_discount_percent: 0,
  tax: { name: 'PB1', percent: 10 },
  tax_amount: 5000,
  amount_after_tax: 55000,
  totalMdrFee: 0,
  payments: [
    {
      id: 1,
      payment_type: { name: 'Tunai' },
      payment_type_name: 'Tunai', // Fallback untuk kompatibilitas
      amount_after_tax: 55000,
      payment_fee: 0,
      payback: 10000,
    },
  ],
  payment_amount: 55000,
  payment_back_amount: 10000,
  message: 'Terima kasih atas kunjungan Anda!',
}

/**
 * Get user's preferred print method from localStorage
 */
export const getPreferredPrintMethod = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (error) {
    console.error('Error reading printer preference:', error)
  }
  return null
}

/**
 * Set user's preferred print method
 */
export const setPreferredPrintMethod = (method, printerName = null, device = null) => {
  try {
    const preference = {
      method,
      printerName,
      deviceName: device?.name || null,
      timestamp: Date.now(),
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preference))
    console.log('Printer preference saved:', preference)
  } catch (error) {
    console.error('Error saving printer preference:', error)
  }
}

/**
 * Clear printer preference
 */
export const clearPrinterPreference = () => {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.error('Error clearing printer preference:', error)
  }
}

/**
 * Detect available print methods
 */
export const detectAvailablePrintMethods = async () => {
  const methods = []

  // Check QZ Tray
  try {
    const qzStatus = await qzTrayPrinter.checkStatus()
    if (qzStatus.available && qzStatus.connected) {
      methods.push({
        method: PRINT_METHODS.QZ_TRAY,
        name: 'QZ Tray',
        description: 'Support USB, Network, dan Bluetooth printer',
        available: true,
        status: qzStatus,
        priority: 1,
      })
    }
  } catch (error) {
    console.log('QZ Tray not available:', error.message)
  }

  return methods.sort((a, b) => a.priority - b.priority)
}

/**
 * Get best available print method
 */
export const getBestPrintMethod = async () => {
  // Check user preference first
  const preference = getPreferredPrintMethod()
  if (preference) {
    // Validate preference is still available
    const methods = await detectAvailablePrintMethods()
    const preferredMethod = methods.find((m) => m.method === preference.method)
    if (preferredMethod?.available) {
      console.log('Using preferred print method:', preference.method)
      return preference.method
    }
  }

  // Auto-detect best method
  const methods = await detectAvailablePrintMethods()

  if (methods.length === 0) {
    throw new Error('Tidak ada metode print yang tersedia')
  }

  console.log('Auto-detected print method:', methods[0].method)
  return methods[0].method
}

/**
 * Main print function - auto-selects best method
 */
export const printReceipt = async (receiptData, options = {}, formatMessage) => {
  try {
    // Prepare print options
    const printOptions = {
      printerFunction: options.printerFunction || 'struk',
      outlet: options.outlet || {},
      companyOptions: options.companyOptions || {},
      outletOptions: options.outletOptions || {},
      printerName: options.printerName,
      device: options.device,
      paperWidth: options.paperWidth, // '80mm', '58mm', or number (default: 42)
      enableCut: options.enableCut,
      ...options,
    }

    const result = await qzTrayPrinter.print(receiptData, printOptions, formatMessage)

    return result
  } catch (error) {
    // If failed, try fallback to browser print
    if (options.method !== PRINT_METHODS.BROWSER && !options.noFallback) {
      try {
        return await browserPrinter.print(receiptData, options)
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError)
      }
    }

    throw error
  }
}

/**
 * Print with specific method
 */
export const printWithMethod = async (method, receiptData, options = {}) => {
  return printReceipt(receiptData, { ...options, method })
}

/**
 * Print via QZ Tray
 */
export const printViaQZTray = async (receiptData, options = {}) => {
  return printWithMethod(PRINT_METHODS.QZ_TRAY, receiptData, options)
}

/**
 * Print via Bluetooth
 */
export const printViaBluetooth = async (receiptData, options = {}) => {
  return printWithMethod(PRINT_METHODS.BLUETOOTH, receiptData, options)
}

/**
 * Print via Browser
 */
export const printViaBrowser = async (receiptData, options = {}) => {
  return printWithMethod(PRINT_METHODS.BROWSER, receiptData, options)
}

/**
 * Get available printers (for QZ Tray only)
 */
export const getAvailablePrinters = async () => {
  try {
    const printers = await qzTrayPrinter.getPrinters()
    return printers
  } catch (error) {
    console.error('Error getting printers:', error)
    return []
  }
}

/**
 * Test print
 */
export const testPrint = async (method = null, printerName = null) => {
  try {
    if (!method) {
      method = await getBestPrintMethod()
    }

    console.log('Testing print with method:', method)

    switch (method) {
      case PRINT_METHODS.QZ_TRAY:
        return await qzTrayPrinter.testPrint(printerName)

      case PRINT_METHODS.BROWSER: {
        // Create dummy test data
        const testData = {
          ref_number: 'TEST-001',
          trans_date: new Date().toISOString(),
          user: { name: 'Test User' },
          items: [
            {
              id: 1,
              product: { name: 'Test Product' },
              qty: 1,
              price: 10000,
              amount: 10000,
            },
          ],
          amount: 10000,
          amount_after_tax: 10000,
          payment_amount: 10000,
          message: 'Test Print',
        }
        return await browserPrinter.print(testData, {})
      }

      default:
        throw new Error('Metode print tidak dikenal')
    }
  } catch (error) {
    console.error('Test print failed:', error)
    throw error
  }
}

/**
 * Get printer status
 */
export const getPrinterStatus = async () => {
  const methods = await detectAvailablePrintMethods()
  const preference = getPreferredPrintMethod()

  return {
    availableMethods: methods,
    preferredMethod: preference,
    bestMethod: methods.length > 0 ? methods[0].method : null,
  }
}

export default {
  // Main functions
  print: printReceipt,
  printViaQZTray,
  printViaBluetooth,
  printViaBrowser,
  testPrint,

  // Detection
  detectAvailablePrintMethods,
  getBestPrintMethod,
  getAvailablePrinters,
  getPrinterStatus,

  // Preferences
  getPreferredPrintMethod,
  setPreferredPrintMethod,
  clearPrinterPreference,

  // Print methods enum
  PRINT_METHODS,

  // Direct access to specific printers
  qzTray: qzTrayPrinter,
  browser: browserPrinter,
  mockReceiptData,
}
